import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { articleCardImages } from '@/mocks/articleImages';

export default function CategoryPage() {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const [activeSlug, setActiveSlug] = useState(categorySlug || categories[0].slug);

  useEffect(() => {
    if (categorySlug) {
      setActiveSlug(categorySlug);
    }
  }, [categorySlug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeSlug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const filteredArticles = articles.filter((a) => a.category === activeSlug);

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${activeCategory.name} - 性事咨询员`,
    description: activeCategory.description,
    url: `https://xingshizixun.com/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '性事咨询员',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageSchema) }}
      />
      <ScrollToTop />
      <Navbar />
      <Breadcrumb />
      <main className="max-w-7xl mx-auto px-4 md:px-6 pb-16">
        <header className="py-10 md:py-14">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-3">
            {activeCategory.name}
          </h1>
          <p className="text-foreground-600 max-w-2xl leading-relaxed">
            {activeCategory.description}
          </p>
        </header>

        <div className="flex flex-col lg:flex-row gap-8">
          <aside className="lg:w-64 shrink-0">
            <nav className="lg:sticky lg:top-24">
              <h4 className="text-sm font-semibold text-foreground-800 mb-3">服务分类</h4>
              <ul className="space-y-1">
                {categories.map((cat) => (
                  <li key={cat.slug}>
                    <Link
                      to={`/category/${cat.slug}`}
                      onClick={() => setActiveSlug(cat.slug)}
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 whitespace-nowrap ${
                        activeSlug === cat.slug
                          ? 'bg-primary-100 text-primary-700 font-medium'
                          : 'text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${cat.icon} text-sm`}></i>
                      </div>
                      {cat.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          <section className="flex-1 min-w-0">
            <div className="grid grid-cols-1 gap-4">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group flex flex-col sm:flex-row gap-5 p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-200"
                >
                  <div className="sm:w-48 h-36 shrink-0 rounded-lg overflow-hidden">
                    <img
                      src={articleCardImages[article.imageSeq]}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-3 line-clamp-2">
                      {article.summary}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line"></i> {article.author}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i> {article.publishDate}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i> {article.readTime}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {filteredArticles.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                  <i className="ri-article-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}